import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";
import { useTranscribeFeedback, useTranscribeProfile } from "@/api/hooks";
import { ApiError } from "@/api/client";

/**
 * VoiceBioButton — «Розповісти голосом» (юзер 2026-06-14).
 * Запис мікрофона → base64 → /profile/transcribe (Whisper + AI-структурування)
 * → onResult(text). Використовується в полі «Про тебе» (профіль) і в формі
 * профілю всередині AI-попапа.
 */

const MAX_MS = 120_000; // 2 хв ліміт запису

function arrayBufferToBase64(buf: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function VoiceBioButton({
  onResult,
  mode = "bio",
  label,
  size = "md",
}: {
  onResult: (text: string) => void;
  /** `bio` — розшифровка + структурування під «Про тебе» (профіль).
   *  `raw` — сира розшифровка (опитування): текст дописується до вже
   *  написаного, тому структурувати його не можна (owner 2026-08-11). */
  mode?: "bio" | "raw";
  label?: string;
  /** `lg` — кегль як у головної кнопки поруч (owner 2026-08-11: текст у
   *  голосовій кнопці був дрібніший за «Надіслати відгук»). */
  size?: "md" | "lg";
}) {
  // Один набір класів на всі три стани кнопки (спокій / запис / розпізнавання),
  // інакше вони знову розʼїдуться між собою.
  const sizeCls = size === "lg" ? "py-3.5 text-[0.9375rem]" : "py-3 text-[0.84375rem]";
  const bioMut = useTranscribeProfile();
  const rawMut = useTranscribeFeedback();
  const transcribeMut = mode === "raw" ? rawMut : bioMut;
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mrRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
      try {
        mrRef.current?.stream.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
    };
  }, []);

  const stop = () => {
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    try {
      mrRef.current?.stop();
    } catch {
      /* ignore */
    }
  };

  const start = async () => {
    if (recording || transcribeMut.isPending) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      showAppAlert("Запис голосу недоступний на цьому пристрої.", {
        title: "Мікрофон",
        emoji: "🎤",
      });
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      // Android: тапнути «Дозволити» у попапі WebView недостатньо, якщо у
      // самого застосунку Telegram немає дозволу на мікрофон в ОС → getUserMedia
      // далі кидає NotAllowedError скільки б разів юзер не тиснув «Дозволити».
      const name = (err as DOMException)?.name || "";
      let msg = "Дозволь доступ до мікрофона, щоб надиктувати про себе.";
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg =
          "Доступ до мікрофона заблоковано. Увімкни мікрофон для застосунку " +
          "Telegram у налаштуваннях телефона (Android: Налаштування → Додатки " +
          "→ Telegram → Дозволи → Мікрофон), потім спробуй ще раз.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "Мікрофон не знайдено на цьому пристрої.";
      } else if (name === "NotReadableError" || name === "AbortError") {
        msg =
          "Мікрофон зараз зайнятий іншим додатком. Закрий інші додатки, що " +
          "використовують мікрофон, і спробуй ще раз.";
      } else if (name) {
        msg = `Не вдалося увімкнути мікрофон (${name}). Перевір дозволи й спробуй ще раз.`;
      }
      showAppAlert(msg, { title: "Потрібен мікрофон", emoji: "🎤" });
      return;
    }
    // MediaRecorder: Android WebView не завжди підтримує дефолтний audio/webm —
    // конструктор без mimeType може кинути NotSupportedError. Підбираємо перший
    // підтримуваний контейнер, інакше fallback на дефолт.
    let mr: MediaRecorder;
    try {
      const types = ["audio/webm", "audio/mp4", "audio/ogg"];
      const picked = types.find(
        (t) => window.MediaRecorder?.isTypeSupported?.(t),
      );
      mr = picked
        ? new MediaRecorder(stream, { mimeType: picked })
        : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      showAppAlert(
        "Запис голосу не підтримується у цьому Telegram. Онови застосунок або " +
          "спробуй з іншого пристрою.",
        { title: "Мікрофон", emoji: "🎤" },
      );
      return;
    }
    mrRef.current = mr;
    chunksRef.current = [];
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const type = mr.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      if (blob.size < 800) {
        showAppAlert("Запис надто короткий — розкажи трохи більше.", {
          title: "Замало",
        });
        return;
      }
      try {
        const buf = await blob.arrayBuffer();
        const b64 = arrayBufferToBase64(buf);
        transcribeMut.mutate(
          { audio_base64: b64, mime: type },
          {
            onSuccess: (r) => {
              tg()?.HapticFeedback?.notificationOccurred?.("success");
              if (r.text) onResult(r.text);
            },
            onError: (e) => {
              tg()?.HapticFeedback?.notificationOccurred?.("error");
              const err = e as ApiError;
              showAppAlert(err?.message || "Не вдалося розпізнати голос.", {
                title: "Голосовий ввід",
                emoji: "🎤",
              });
            },
          },
        );
      } catch {
        showAppAlert("Не вдалося обробити запис. Спробуй ще раз.", {
          title: "Помилка",
        });
      }
    };
    tg()?.HapticFeedback?.impactOccurred?.("medium");
    mr.start();
    setRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    autoStopRef.current = setTimeout(stop, MAX_MS);
  };

  if (transcribeMut.isPending) {
    return (
      <div className={"flex w-full items-center justify-center gap-2 rounded-full bg-surface-soft border border-ink/10 font-display font-bold text-ink-dim " + sizeCls}>
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        Розпізнаю голос…
      </div>
    );
  }

  if (recording) {
    const mm = String(Math.floor(elapsed / 60)).padStart(1, "0");
    const ss = String(elapsed % 60).padStart(2, "0");
    return (
      <button
        type="button"
        onClick={stop}
        className={"flex w-full items-center justify-center gap-2 rounded-full bg-red-500 font-display font-bold text-white active:scale-[0.98] transition " + sizeCls}
      >
        <span className="h-2.5 w-2.5 rounded-full bg-white animate-pulse" />
        Стоп · {mm}:{ss}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      className={"flex w-full items-center justify-center gap-2 rounded-full bg-surface-soft border border-primary/25 font-display font-bold text-primary active:scale-[0.98] transition " + sizeCls}
    >
      <Icon name="waveform" size={16} color="#FE6B04" />
      {label ?? "Розповісти голосом"}
    </button>
  );
}
